import type { MouseEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import type { PeriodSummaryMode } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryMode.ts";
import { useReadCalendarDaysForPeriodSummary } from "@/pages/human-resources/time-management/services/hooks/useReadCalendarDaysForPeriodSummary.ts";
import type { DropDownMenuItemProps } from "@/shared/components/generic/dropdown-menu/DropDownMenuItem";
import type { PeriodSummaryRange } from "@/pages/human-resources/time-management/business/analysis/PeriodSummaryRange.ts";
import useDateFormat from "@/shared/utils/useDateFormat.ts";
import { DateStyle } from "@/shared/utils/Dates.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import { isEmpty } from "@/shared/utils/Strings.ts";

interface Controller {
  count: number;
  open: boolean;
  anchorEl: HTMLElement | null;
  handleOpen: (e: MouseEvent<HTMLButtonElement>) => void;
  handleClose: () => void;
  menuItemProps: DropDownMenuItemProps[];
}

export function usePeriodEventsDropdownController(
  siteId: string,
  date: Date,
  mode: PeriodSummaryMode,
  range: PeriodSummaryRange,
): Controller {
  const calendarDays = useReadCalendarDaysForPeriodSummary(
    siteId,
    date,
    mode,
    range,
  );
  const formatDate = useDateFormat(
    DateStyle.WEEKDAY_SHORT_DATE_MONTH_SHORT,
    LEGACY_TIME_ZONE,
  );

  const items = useMemo(() => {
    const res: { name: string; comment?: string; date: Date }[] = [];
    calendarDays.forEach((calendarDay) => {
      if (calendarDay.publicHoliday != undefined)
        res.push({
          name: calendarDay.publicHoliday,
          date: calendarDay.date,
        });
      for (const ev of calendarDay.events)
        res.push({
          name: ev.name,
          comment: ev.comment,
          date: calendarDay.date,
        });
    });
    return res;
  }, [calendarDays]);

  const count = items.length;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (count === 0) return;
      setAnchorEl(e.currentTarget);
    },
    [count],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const menuItemProps: DropDownMenuItemProps[] = useMemo(
    () =>
      items.map((it) => {
        let label = it.name;
        if (range !== "DAY") {
          label = `${formatDate(it.date)} : ` + label;
        }
        if (!isEmpty(it.comment)) {
          label += ` — ${it.comment}`;
        }
        return {
          label,
          onClick: handleClose,
        };
      }),
    [items, handleClose, formatDate, range],
  );

  return {
    count,
    open,
    anchorEl,
    handleOpen,
    handleClose,
    menuItemProps,
  };
}
